// canvas/table.js: table renderer (plan-m2.md v2 M2.c-1; trimmed in M2.c-5).
//
// Pure JS: takes a 2D canvas ctx + a state snapshot + a TableLayout +
// optional settings, draws felt, 4 nameplates, active-player highlight.
// The trump badge moved out to `canvas/trump-badge.js` in M2.c-5; this
// module still calls it from `renderTable` so the public surface is
// unchanged for callers.
//
// State snapshot shape used here (subset of docs/m2-snapshot-schema.md):
//   { current_turn_seat: 1|2|3|4|nil,
//     trump_suit:        'S'|'H'|'D'|'C'|nil,
//     maker_seat:        1|2|3|4|nil }
//
// Accessibility (synthesis OQ-7, plan AC #4): callers must mirror trump
// + current-turn into the canvas aria-label. This module emits
// describeAriaLabel(state) as a helper so App.jsx can set it once and
// keep it in sync.

import { renderTrumpBadge } from './trump-badge.js';
import { _internals as cardInternals } from './cards.js';
import { canvasFont, fitFontSize, truncateLabelToFit } from './text.js';

const { SUIT_NAME } = cardInternals;

const COLOR_FELT_TOP    = '#2A1F2F';
const COLOR_FELT_BOTTOM = '#1A1422';
// T5 felt surface, the table is a bounded object on the page, not a
// full-bleed wash. Page color sits behind the rim; the felt gets a
// radial lift at center, a noise tile for texture, and a vignette.
const COLOR_PAGE_BG       = '#14101C';
const COLOR_FELT_CENTER   = '#322640';
const COLOR_FELT_RIM      = '#0E0A14';
const FELT_INSET  = 10;
const FELT_RADIUS = 26;
const NOISE_TILE_SIZE = 64;
const COLOR_PLATE_BG    = '#2A2A2A';
const COLOR_PLATE_TEXT  = '#F0F0F0';
const COLOR_TEAM_GOLD   = '#F0C040';  // "us" the viewer's team
const COLOR_TEAM_TEAL   = '#3DC4C7';  // "them" the opponents
// T8: the active-turn ring is WHITE, gold meant both "your team" and
// "active", so a gold glow on a teal opponent plate read as a team
// signal. White is turn-only; team color stays on the border channel.
const COLOR_GLOW_ACTIVE = '#FFFFFF';
const INACTIVE_PLATE_ALPHA = 0.62;  // dim the three waiting seats
// B4b-1 A2: the loner's plate carries an "ALONE" pill under the name,
// in the Go Alone button's gold-orange (bidding-logic.js).
const COLOR_ALONE_BG    = '#F0A030';
const COLOR_ALONE_TEXT  = '#1A1422';
const ALONE_MARK_TEXT   = 'ALONE';
const ALONE_MARK_H      = 12;
// The states of a live hand after the bid: the loner mark shows only here
// and clears when the hand ends (end_of_hand / score).
const LIVE_HAND_STATES = new Set(['dealer_discard', 'trick_play']);

// Team A = seats 1+3. Team B = seats 2+4. Per state.lua line 56:
// partner_of(seat) = ((seat+1)%4)+1.
function teamOf(seat) { return (seat === 1 || seat === 3) ? 'A' : 'B'; }
function teamKeyOf(seat) { return teamOf(seat) === 'A' ? 'team_a' : 'team_b'; }
// Border color is PERSPECTIVE-relative so it matches the scoreboard's
// US=gold / THEM=teal (FOLLOWUPS #6): gold = the viewer's team, teal =
// the opponents, whichever physical team the viewer sits on. Defaults
// to team_a so the hot-seat (human always at seat 1) and the decorative
// hero keep seats 1+3 gold with no caller change.
function borderColor(seat, myTeam = 'team_a') {
  return teamKeyOf(seat) === myTeam ? COLOR_TEAM_GOLD : COLOR_TEAM_TEAL;
}

// Placeholder labels, M3+ swaps in real player identity. Indexed by seat ID.
const DEFAULT_SEAT_LABELS = {
  1: 'Player 1',
  2: 'Bot Atom',
  3: 'Bot Caprica',
  4: 'Bot HAL',
};

function fillRoundRect(ctx, x, y, w, h, r) {
  ctx.beginPath();
  ctx.moveTo(x + r, y);
  ctx.arcTo(x + w, y,     x + w, y + h, r);
  ctx.arcTo(x + w, y + h, x,     y + h, r);
  ctx.arcTo(x,     y + h, x,     y,     r);
  ctx.arcTo(x,     y,     x + w, y,     r);
  ctx.closePath();
  ctx.fill();
}

function strokeRoundRect(ctx, x, y, w, h, r) {
  ctx.beginPath();
  ctx.moveTo(x + r, y);
  ctx.arcTo(x + w, y,     x + w, y + h, r);
  ctx.arcTo(x + w, y + h, x,     y + h, r);
  ctx.arcTo(x,     y + h, x,     y,     r);
  ctx.arcTo(x,     y,     x + w, y,     r);
  ctx.closePath();
  ctx.stroke();
}

function roundRectPath(ctx, x, y, w, h, r) {
  ctx.beginPath();
  ctx.moveTo(x + r, y);
  ctx.arcTo(x + w, y,     x + w, y + h, r);
  ctx.arcTo(x + w, y + h, x,     y + h, r);
  ctx.arcTo(x,     y + h, x,     y,     r);
  ctx.arcTo(x,     y,     x + w, y,     r);
  ctx.closePath();
}

// One noise tile per session: sparse light/dark flecks at very low
// alpha, pattern-tiled across the felt for cloth texture. Generated
// lazily so importing this module in Node never touches `document`.
let noiseTile = null;
function getNoiseTile() {
  if (noiseTile) return noiseTile;
  const tile = document.createElement('canvas');
  tile.width = NOISE_TILE_SIZE;
  tile.height = NOISE_TILE_SIZE;
  const tctx = /** @type {CanvasRenderingContext2D} */ (tile.getContext('2d'));
  const img = tctx.createImageData(NOISE_TILE_SIZE, NOISE_TILE_SIZE);
  for (let i = 0; i < img.data.length; i += 4) {
    const v = Math.random();
    let shade = 0;
    let alpha = 0;
    if (v > 0.93) { shade = 255; alpha = 9; }   // light flecks
    else if (v < 0.07) { shade = 0; alpha = 13; } // dark flecks
    img.data[i] = shade;
    img.data[i + 1] = shade;
    img.data[i + 2] = shade;
    img.data[i + 3] = alpha;
  }
  tctx.putImageData(img, 0, 0);
  noiseTile = tile;
  return tile;
}

// Felt surface cache: the composed table (page bg + rim + felt + light +
// noise + vignette) is static per canvas size, so render it once to an
// offscreen canvas and blit per frame. Single entry, resizes are rare.
let feltCache = null;

function renderFeltSurface(layout, scale) {
  const { canvasW: w, canvasH: h } = layout;
  const surface = document.createElement('canvas');
  // Same density as the game canvas (CSS size x devicePixelRatio), so the
  // blit below copies backing pixels 1:1 instead of stretching them.
  surface.width = Math.round(w * scale);
  surface.height = Math.round(h * scale);
  const ctx = /** @type {CanvasRenderingContext2D} */ (surface.getContext('2d'));
  ctx.setTransform(scale, 0, 0, scale, 0, 0);

  // Page behind the table rim.
  ctx.fillStyle = COLOR_PAGE_BG;
  ctx.fillRect(0, 0, w, h);

  const fx = FELT_INSET;
  const fy = FELT_INSET;
  const fw = w - 2 * FELT_INSET;
  const fh = h - 2 * FELT_INSET;

  // Rim: a darker rounded slab 2px proud of the felt.
  ctx.fillStyle = COLOR_FELT_RIM;
  roundRectPath(ctx, fx - 3, fy - 3, fw + 6, fh + 6, FELT_RADIUS + 3);
  ctx.fill();

  ctx.save();
  roundRectPath(ctx, fx, fy, fw, fh, FELT_RADIUS);
  ctx.clip();

  // Base felt, keeps the M2 gradient endpoints (#14 card-back contrast
  // guard in cards.test.js pins against these constants).
  const grad = ctx.createLinearGradient(0, fy, 0, fy + fh);
  grad.addColorStop(0, COLOR_FELT_TOP);
  grad.addColorStop(1, COLOR_FELT_BOTTOM);
  ctx.fillStyle = grad;
  ctx.fillRect(fx, fy, fw, fh);

  // Radial light, table lamp over the trick area (slightly above center).
  const lx = w / 2;
  const ly = fy + fh * 0.44;
  const lr = Math.max(fw, fh) * 0.62;
  const light = ctx.createRadialGradient(lx, ly, 0, lx, ly, lr);
  light.addColorStop(0, COLOR_FELT_CENTER);
  light.addColorStop(1, 'rgba(50, 38, 64, 0)');
  ctx.fillStyle = light;
  ctx.fillRect(fx, fy, fw, fh);

  // Cloth noise.
  const pattern = ctx.createPattern(getNoiseTile(), 'repeat');
  if (pattern) {
    ctx.fillStyle = pattern;
    ctx.fillRect(fx, fy, fw, fh);
  }

  // Vignette, corners fall off so the table reads as lit from above.
  const vg = ctx.createRadialGradient(lx, ly, lr * 0.45, lx, ly, lr * 1.05);
  vg.addColorStop(0, 'rgba(0, 0, 0, 0)');
  vg.addColorStop(1, 'rgba(0, 0, 0, 0.38)');
  ctx.fillStyle = vg;
  ctx.fillRect(fx, fy, fw, fh);

  // Inner edge highlight, a faint top-light catch on the felt lip.
  ctx.strokeStyle = 'rgba(240, 192, 64, 0.07)';
  ctx.lineWidth = 1.5;
  roundRectPath(ctx, fx + 1, fy + 1, fw - 2, fh - 2, FELT_RADIUS - 1);
  ctx.stroke();

  ctx.restore();
  return surface;
}

function drawFelt(ctx, layout) {
  // Non-DOM fallback (Node tests with a mock ctx): the M2 flat gradient.
  if (typeof document === 'undefined') {
    const grad = ctx.createLinearGradient(0, 0, 0, layout.canvasH);
    grad.addColorStop(0, COLOR_FELT_TOP);
    grad.addColorStop(1, COLOR_FELT_BOTTOM);
    ctx.fillStyle = grad;
    ctx.fillRect(0, 0, layout.canvasW, layout.canvasH);
    return;
  }
  // The game canvas context is scaled by devicePixelRatio (hidpi.js);
  // build the felt at that scale. A rotated or skewed transform never
  // reaches here (renderTable runs on the plain DPR transform).
  const m = ctx.getTransform();
  if (m.b !== 0 || m.c !== 0 || !(m.a > 0)) {
    throw new Error(`drawFelt: expected a plain scale transform, got ${JSON.stringify([m.a, m.b, m.c, m.d])}`);
  }
  const scale = m.a;
  const key = `${layout.canvasW}x${layout.canvasH}@${scale}`;
  if (!feltCache || feltCache.key !== key) {
    feltCache = { key, canvas: renderFeltSurface(layout, scale) };
  }
  ctx.drawImage(feltCache.canvas, 0, 0, layout.canvasW, layout.canvasH);
}

/**
 * B4b-1 A2: the lua seat going alone this hand, or null. Only while the
 * hand is live, so the mark clears when the hand ends.
 *
 * @param {{current_state?: string, caller_went_alone?: boolean, maker_seat?: number|null}|null|undefined} state
 * @returns {number|null}
 */
export function lonerSeat(state) {
  if (!state || !state.caller_went_alone || !state.maker_seat) return null;
  return LIVE_HAND_STATES.has(state.current_state ?? '') ? state.maker_seat : null;
}

/**
 * Draw the "ALONE" pill centred at (cx, cy) and return its box.
 *
 * @param {CanvasRenderingContext2D} ctx
 * @param {number} cx
 * @param {number} cy
 */
function drawAloneMark(ctx, cx, cy) {
  ctx.font = canvasFont(8, { weight: 700 });
  const w = Math.ceil(ctx.measureText(ALONE_MARK_TEXT).width) + 10;
  const box = { x: Math.round(cx - w / 2), y: Math.round(cy - ALONE_MARK_H / 2), w, h: ALONE_MARK_H };
  ctx.fillStyle = COLOR_ALONE_BG;
  fillRoundRect(ctx, box.x, box.y, box.w, box.h, ALONE_MARK_H / 2);
  ctx.fillStyle = COLOR_ALONE_TEXT;
  ctx.textAlign = 'center';
  ctx.textBaseline = 'middle';
  ctx.fillText(ALONE_MARK_TEXT, cx, cy + 0.5);
  return box;
}

function drawNameplate(ctx, seat, isActive, label, myTeam, alone = false) {
  const { x, y, w, h } = seat;
  ctx.fillStyle = COLOR_PLATE_BG;
  fillRoundRect(ctx, x, y, w, h, 8);

  // Border color = team color. ALWAYS drawn (color + position double-encode
  // team per OQ-7 accessibility; the position channel survives color-blind
  // viewers). Perspective-relative: gold = the viewer's team.
  ctx.strokeStyle = borderColor(seat.id, myTeam);
  ctx.lineWidth = 2;
  strokeRoundRect(ctx, x + 1, y + 1, w - 2, h - 2, 8);

  // Active-player highlight: outer glow ring + bold underline on label.
  // BOTH channels per OQ-7 (color alone is not the cue). T8: white, not
  // gold, turn and team are separate signals now.
  if (isActive) {
    ctx.save();
    ctx.shadowColor = COLOR_GLOW_ACTIVE;
    ctx.shadowBlur = 14;
    ctx.strokeStyle = COLOR_GLOW_ACTIVE;
    ctx.lineWidth = 3;
    strokeRoundRect(ctx, x - 2, y - 2, w + 4, h + 4, 10);
    ctx.restore();
  }

  // Label, centered. When active, bold + underline.
  // #design-a11y: shrink the label to fit the plate width instead of
  // letting long names ("Seat 2" on a tight plate) clip mid-glyph. We
  // measure at the candidate size each step via the shared helper.
  ctx.fillStyle = COLOR_PLATE_TEXT;
  const weightOpt = isActive ? { weight: 600 } : undefined;
  const labelPad = 8; // px of breathing room on each side inside the plate
  const fontPx = fitFontSize(label, w - labelPad * 2, (px) => {
    ctx.font = canvasFont(px, weightOpt);
    return ctx.measureText(label).width;
  }, { basePx: 14, minPx: 8 });
  ctx.font = canvasFont(fontPx, weightOpt);
  // Still too wide at the smallest size: shorten the name with "…" and
  // keep any " · bot" mark whole (B3a item 6).
  const text = truncateLabelToFit(label, w - labelPad * 2, (t) => ctx.measureText(t).width);
  ctx.textAlign = 'center';
  ctx.textBaseline = 'middle';
  const cx = x + Math.round(w / 2);
  const cy = y + Math.round(h / 2);
  // A2: with the ALONE pill the name moves up 9px and the pill takes
  // the plate's lower part, so on a 44px plate the turn underline still
  // ends 5px above the pill.
  const nameY = alone ? cy - 9 : cy;
  ctx.fillText(text, cx, nameY);

  if (isActive) {
    // Underline width = text width + a couple px of slack.
    const metrics = ctx.measureText(text);
    const underlineW = Math.min(w - 16, metrics.width + 4);
    const underlineX = cx - Math.round(underlineW / 2);
    const underlineY = alone ? nameY + 9 : cy + 10;
    ctx.fillRect(underlineX, underlineY, underlineW, 2);
  }
  // The pill sits 3px from the plate's bottom edge, inside its 2px border.
  return alone ? drawAloneMark(ctx, cx, y + h - 3 - ALONE_MARK_H / 2) : null;
}

/**
 * Render the table: felt, 4 nameplates, active-player highlight, trump badge.
 *
 * @param {CanvasRenderingContext2D} ctx
 * @param {{
 *   current_turn_seat?: number|null,
 *   trump_suit?:        string|null,
 *   maker_seat?:        number|null,
 * }} state
 * @param {import('./layout.js').TableLayout} layout
 * @param {{seatLabels?: Record<number,string>, plainLabels?: Record<number,string>, trumpBadge?: boolean, myTeam?: 'team_a'|'team_b'}} [settings]
 */
export function renderTable(ctx, state, layout, settings = {}) {
  const labels = settings.seatLabels || DEFAULT_SEAT_LABELS;
  const myTeam = settings.myTeam === 'team_b' ? 'team_b' : 'team_a';
  drawFelt(ctx, layout);
  // T8: while someone is on turn, the three waiting plates dim, the
  // active plate is the only one at full strength (plus white ring).
  const someoneActive = state.current_turn_seat != null;
  const loner = lonerSeat(state);
  // B4b-1 e2e seam: "slot:x,y,w,h" of the drawn ALONE pill.
  let aloneMark = null;
  for (const [slot, seat] of layout.seats.entries()) {
    const isActive = state.current_turn_seat === seat.id;
    ctx.save();
    if (someoneActive && !isActive) ctx.globalAlpha = INACTIVE_PLATE_ALPHA;
    const mark = drawNameplate(ctx, seat, isActive, labels[seat.id] || `Seat ${seat.id}`, myTeam, loner === seat.id);
    if (mark) aloneMark = `${slot}:${mark.x},${mark.y},${mark.w},${mark.h}`;
    ctx.restore();
  }
  const el = ctx.canvas;
  if (el && el.dataset) {
    if (aloneMark) el.dataset.aloneMark = aloneMark;
    else delete el.dataset.aloneMark;
  }
  // The trump badge is opt-out: decorative surfaces (the home hero) pass
  // trumpBadge:false to suppress it entirely. Real game tables omit the
  // flag and keep the persistent indicator.
  // B4b-1 A6: the maker's plain name (no "· bot" mark) for the phone label.
  if (settings.trumpBadge !== false) {
    renderTrumpBadge(ctx, layout, state, {
      makerName: state.maker_seat ? settings.plainLabels?.[state.maker_seat] : null,
    });
  }
}

/**
 * Build the aria-label text for the canvas. AC #4 mandates trump + current
 * turn info; we add seat labels so a screen-reader user has the same context
 * a sighted player gets from the visual highlight.
 *
 * @param {{
 *   current_turn_seat?: number|null,
 *   trump_suit?:        string|null,
 * }} state
 * @param {{seatLabels?: Record<number,string>}} [settings]
 * @returns {string}
 */
export function describeAriaLabel(state, settings = {}) {
  const labels = settings.seatLabels || DEFAULT_SEAT_LABELS;
  const trumpDescr = state.trump_suit ? SUIT_NAME[state.trump_suit] : 'none';
  const turnLabel = state.current_turn_seat
    ? (labels[state.current_turn_seat] || `Seat ${state.current_turn_seat}`)
    : 'waiting';
  return `Euchre table. Trump: ${trumpDescr}. Current turn: ${turnLabel}.`;
}

export const _internals = {
  COLOR_TEAM_GOLD,
  COLOR_TEAM_TEAL,
  COLOR_GLOW_ACTIVE,
  INACTIVE_PLATE_ALPHA,
  COLOR_FELT_TOP,
  COLOR_FELT_BOTTOM,
  COLOR_PAGE_BG,
  COLOR_FELT_CENTER,
  FELT_INSET,
  FELT_RADIUS,
  DEFAULT_SEAT_LABELS,
  SUIT_NAME,
  teamOf,
  borderColor,
};
